import { useEffect, useState, useCallback } from 'react';

/**
 * Detects PWA installability + standalone mode + iOS.
 * Provides a `install()` method that triggers the native install prompt.
 *
 * Behaviour notes:
 * - Chrome / Edge / Android-Chrome fire `beforeinstallprompt` — we capture & expose it.
 * - iOS Safari NEVER fires that event — the user must manually use Share → Add to Home Screen.
 *   We return `isIOS: true` so the caller can show the right instructions.
 * - If the app is already installed (matchMedia standalone), `isStandalone: true`.
 */
export default function useInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    // Detect standalone (already-installed) mode
    try {
      const matches = window.matchMedia?.('(display-mode: standalone)')?.matches;
      const iosStandalone = window.navigator.standalone === true;
      setIsStandalone(Boolean(matches || iosStandalone));
    } catch { /* noop */ }

    // Detect iOS (Safari doesn't expose beforeinstallprompt)
    try {
      const ua = window.navigator.userAgent || '';
      const isiPad = /iPad/.test(ua) || (/Macintosh/.test(ua) && 'ontouchend' in document);
      const isiPhone = /iPhone|iPod/.test(ua);
      setIsIOS(isiPad || isiPhone);
    } catch { /* noop */ }

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setDeferred(null);
      setIsStandalone(true);
    };

    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!deferred) return { outcome: 'unavailable' };
    try {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      setDeferred(null);
      return choice;
    } catch (e) {
      return { outcome: 'error', error: e };
    }
  }, [deferred]);

  return {
    canInstall: Boolean(deferred),
    isStandalone,
    isIOS,
    install,
  };
}
